import { useEffect, useId, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { Panel, keepInView } from './DatePicker';

/* "Add a topic" (BBO-121): the dashed pill opens the house drop-down (the
   white panel, rows that go gray under the pointer), the topics not in the set
   grouped by domain, never the browser's own <select>. Arrows walk the topics,
   a letter jumps to the next topic that starts with it, Enter adds, Escape or
   a click outside closes. After an add the box puts the cursor on the new row. */
export default function AddTopicMenu({ topics, groupLabel, onAdd, busy = false, buttonRef }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrap = useRef(null);
  const list = useRef(null);
  const typed = useRef({ text: '', at: 0 });
  const groups = [...new Set(topics.map(t => t.domain))].map(dom => ({ dom, items: topics.filter(t => t.domain === dom) }));
  const flat = groups.flatMap(g => g.items);
  useEffect(() => { if (open) list.current?.focus(); }, [open]);
  useEffect(() => {
    if (open) keepInView(list.current, list.current?.querySelector(`[data-i="${active}"]`));
  }, [active, open]);
  const close = toButton => { setOpen(false); if (toButton) buttonRef?.current?.focus(); };
  const pick = t => { setOpen(false); onAdd(t.id); };
  const onListKey = e => {
    const last = flat.length - 1;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(last, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)); }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
    else if (e.key === 'End') { e.preventDefault(); setActive(last); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (flat[active]) pick(flat[active]); }
    else if (e.key === 'Tab') { e.preventDefault(); close(true); }
    else if (e.key.length === 1 && /\S/.test(e.key)) {
      // type-ahead: the letters typed within a second, from the row after this one
      const now = Date.now();
      typed.current = { text: (now - typed.current.at < 1000 ? typed.current.text : '') + e.key.toLowerCase(), at: now };
      const q = typed.current.text;
      const from = q.length > 1 ? active : active + 1;
      const order = [...flat.slice(from), ...flat.slice(0, from)];
      const hit = order.find(t => t.label.toLowerCase().startsWith(q));
      if (hit) setActive(flat.indexOf(hit));
    }
  };
  return <span ref={wrap} className="psb-add-wrap">
    <button ref={buttonRef} type="button" className="psb-add" aria-haspopup="listbox" aria-expanded={open} disabled={busy}
      onClick={() => { setActive(0); setOpen(o => !o); }}
      onKeyDown={e => { if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); setActive(0); setOpen(true); } }}>
      <Plus size={14} aria-hidden="true" />Add a topic</button>
    {open && <Panel anchor={wrap} role="presentation" className="psb-add-pop" onClose={reason => close(reason === 'escape')}>
      <div ref={list} role="listbox" tabIndex={-1} aria-label="Add a topic" aria-activedescendant={flat[active] ? `${id}-o${active}` : undefined} className="psb-add-list" onKeyDown={onListKey}>
        {groups.map((g, gi) => <div key={g.dom} role="group" aria-labelledby={`${id}-g${gi}`}>
          <div id={`${id}-g${gi}`} className="psb-add-head">{groupLabel(g.dom)}</div>
          {g.items.map(t => {
            const i = flat.indexOf(t);
            return <div key={t.id} id={`${id}-o${i}`} data-i={i} role="option" aria-selected={false} className={`psb-add-opt${i === active ? ' is-active' : ''}`}
              onMouseEnter={() => setActive(i)} onMouseDown={e => e.preventDefault()} onClick={() => pick(t)}>{t.label}</div>;
          })}
        </div>)}
      </div>
    </Panel>}
  </span>;
}
